import {
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { AssistantMessage, AssistantRunRef } from '@core/models/assistant/assistant.model';
import { AssistantService } from '@core/services/assistant/assistant.service';
import { ICONS } from '@core/services/icons/icons.service';

import { AssistantApprovalCardComponent } from '../assistant-approval-card/assistant-approval-card.component';
import {
  AssistantContextTextComponent,
  CONTEXT_ICONS
} from '../assistant-context/assistant-context-text.component';
import { AssistantFileCardComponent } from '../assistant-file/assistant-file-card.component';
import { AssistantRunCardComponent } from '../assistant-run-card/assistant-run-card.component';
import { AssistantToolGroupComponent } from '../assistant-tool-group/assistant-tool-group.component';
import { AssistantToolLineComponent } from '../assistant-tool-line/assistant-tool-line.component';
import { createAssistantMarkdown } from './assistant-markdown';
import { StreamingMarkdown } from './streaming-markdown.utils';

@Component({
  selector: 'app-assistant-message',
  imports: [
    TranslatePipe,
    AssistantApprovalCardComponent,
    AssistantContextTextComponent,
    AssistantRunCardComponent,
    AssistantFileCardComponent,
    AssistantToolGroupComponent,
    AssistantToolLineComponent
  ],
  templateUrl: './assistant-message.component.html',
  host: { class: 'group/message block' },
  styles: ``
})
export class AssistantMessageComponent {
  private readonly translate = inject(TranslateService);
  protected readonly assistant = inject(AssistantService);
  readonly message = input.required<AssistantMessage>();
  readonly showContext = input(false);
  /** The newest message of the conversation, whose actions stay visible. */
  readonly last = input(false);
  private readonly injector = inject(Injector);
  protected readonly editing = signal(false);
  protected readonly draft = signal('');
  protected readonly copied = signal(false);
  private readonly editButton = viewChild<ElementRef<HTMLButtonElement>>('editButton');
  private readonly editBox = viewChild<ElementRef<HTMLTextAreaElement>>('editBox');
  protected readonly ICONS = ICONS;
  protected readonly contextIcons = CONTEXT_ICONS;
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((e) => e.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );
  private readonly markdown = createAssistantMarkdown({
    rowCount: (rows) => this.translate.instant('assistant.table_rows', { count: rows }),
    copyLabel: () => this.translate.instant('assistant.chrome.copy')
  });
  private readonly streamed = new StreamingMarkdown(this.markdown);
  protected readonly html = computed(() => {
    this.currentLang();
    const { text, status } = this.message();
    return status === 'streaming'
      ? this.streamed.render(text)
      : (this.markdown.parse(text) as string);
  });

  protected readonly readTools = computed(() =>
    this.message().tools.filter((t) => t.access === 'read')
  );
  protected readonly changeTools = computed(() =>
    this.message().tools.filter((t) => t.access !== 'read')
  );

  protected readonly pendingCount = computed(
    () => (this.message().approvals ?? []).filter((c) => c.state === 'pending').length
  );

  protected parameterOf(run: AssistantRunRef): string | null {
    const param = this.message().tools.find((t) => t.callId === run.callId)?.arguments[
      'param_id'
    ];
    return param === undefined || param === null ? null : String(param);
  }

  protected onContentClick(event: Event): void {
    const control = (event.target as Element | null)?.closest<HTMLElement>(
      '.assistant-copy-code'
    );
    if (!control) return;
    const code =
      control.closest('.assistant-code')?.querySelector('code')?.textContent ?? '';
    const label = control.querySelector('.assistant-copy-label');
    void this.assistant.copyText(code).then((copied) => {
      if (!copied || !label) return;
      label.textContent = this.translate.instant('assistant.chrome.copied');
      setTimeout(
        () => (label.textContent = this.translate.instant('assistant.chrome.copy')),
        1500
      );
    });
  }

  protected onContentKey(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target as Element | null)?.closest('.assistant-copy-code')) return;
    event.preventDefault();
    this.onContentClick(event);
  }

  protected copyMessage(): void {
    void this.assistant.copyText(this.message().text).then((done) => {
      if (!done) return;
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1500);
    });
  }

  protected startEdit(): void {
    this.draft.set(this.message().text);
    this.editing.set(true);
    afterNextRender(() => this.editBox()?.nativeElement.focus(), {
      injector: this.injector
    });
  }

  protected cancelEdit(): void {
    this.editing.set(false);
    afterNextRender(() => this.editButton()?.nativeElement.focus(), {
      injector: this.injector
    });
  }

  protected sendEdit(): void {
    const text = this.draft().trim();
    if (!text) return;
    this.editing.set(false);
    void this.assistant.editAndResend(this.message().id, text);
  }

  protected onEditKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      // Otherwise the panel's own Escape handler closes the whole panel.
      event.stopPropagation();
      this.cancelEdit();
    } else if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.sendEdit();
    }
  }
}
